import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import FilingCard from './FilingCard';
import FileNowModal from './FileNowModal';
import TransferToFirmModal from './TransferToFirmModal';
import AssignToStaffModal from './AssignToStaffModal';
import filingService from '../../services/filingService';
import { useAssignApplication, useUnassignApplication, useTransferApplication, useRecallApplication, useOrgMembers, FILING_QUEUE_KEY } from '../../hooks/useFilingQueue';
import { useExternalFirms } from '../../../hooks/queries/useLawFirms';
import { useAttorney } from '../../context/AttorneyContext';
import './FilingQueue.css';

/**
 * Filing Queue Component
 * Main view for attorneys to see pending filings
 * Supports ?app={applicationId} URL param to highlight a specific application
 */
function FilingQueue() {
  const { t } = useTranslation('attorney');
  const queryClient = useQueryClient();
  const { canTransferToFirm, canRecallFromFirm, canReassign, attorneyId, isAttorneyAdmin } = useAttorney();

  const [selectedCountry, setSelectedCountry] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('pending');
  const [assignmentFilter, setAssignmentFilter] = useState('all');
  const [selectedFiling, setSelectedFiling] = useState(null);
  const [viewMode, setViewMode] = useState(false); // true when viewing filed jurisdiction
  const [editingRef, setEditingRef] = useState(null); // { application, jurisdiction } when editing ref
  const [highlightedAppId, setHighlightedAppId] = useState(null);
  const [transferModalApp, setTransferModalApp] = useState(null);
  const [assignStaffModalApp, setAssignStaffModalApp] = useState(null);
  const highlightedRef = useRef(null);

  // Assignment mutations
  const assignMutation = useAssignApplication();
  const unassignMutation = useUnassignApplication();
  const transferMutation = useTransferApplication();
  const recallMutation = useRecallApplication();

  // External firms for transfer dropdown
  const { data: externalFirmsData } = useExternalFirms({ enabled: canTransferToFirm });

  // Org members for staff assignment dropdown (admins only)
  const { data: orgMembersData } = useOrgMembers({ enabled: canReassign });

  // Read URL params on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const appId = urlParams.get('app');
    if (appId) {
      setHighlightedAppId(appId);
      // Show all statuses to ensure we can find the app
      setSelectedStatus('all');
    }
  }, []);

  // Fetch filing queue using service layer
  const { data, isLoading, error } = useQuery({
    queryKey: [...FILING_QUEUE_KEY, selectedCountry, selectedStatus, assignmentFilter],
    queryFn: () => filingService.getFilingQueue({
      jurisdiction: selectedCountry !== 'all' ? selectedCountry : undefined,
      status: selectedStatus !== 'all' ? selectedStatus : undefined,
      assignmentFilter: assignmentFilter !== 'all' ? assignmentFilter : undefined
    })
  });

  // Extract queue from response
  const filings = data?.queue || [];

  // Scroll to highlighted app when data loads
  useEffect(() => {
    if (highlightedAppId && filings.length > 0 && highlightedRef.current) {
      highlightedRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [highlightedAppId, filings]);

  // Mark as filed mutation using service layer
  const markFiledMutation = useMutation({
    mutationFn: ({ applicationId, jurisdiction, ipOfficeRef }) =>
      filingService.markAsFiled(applicationId, { jurisdiction, ipOfficeRef }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FILING_QUEUE_KEY });
      setSelectedFiling(null);
      setViewMode(false);
    }
  });

  // Update IP Office ref mutation
  const updateRefMutation = useMutation({
    mutationFn: ({ applicationId, jurisdiction, ipOfficeRef }) =>
      filingService.updateIpOfficeRef(applicationId, jurisdiction, ipOfficeRef),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FILING_QUEUE_KEY });
      setEditingRef(null);
    }
  });

  const handleFileNow = (application, jurisdiction) => {
    setSelectedFiling({ application, jurisdiction });
    setViewMode(false);
  };

  const handleViewFiling = (application, jurisdiction) => {
    setSelectedFiling({ application, jurisdiction });
    setViewMode(true);
  };

  const handleEditRef = (application, jurisdiction) => {
    const newRef = window.prompt(
      t('attorney.filingQueue.editRefPrompt', 'Enter new IP Office reference:'),
      jurisdiction.ipOfficeRef || ''
    );
    if (newRef !== null && newRef.trim() !== '') {
      updateRefMutation.mutate({
        applicationId: application.id,
        jurisdiction: jurisdiction.jurisdiction,
        ipOfficeRef: newRef.trim()
      });
    }
  };

  const handleMarkFiled = (ipOfficeRef) => {
    if (selectedFiling) {
      markFiledMutation.mutate({
        applicationId: selectedFiling.application.id,
        jurisdiction: selectedFiling.jurisdiction.jurisdiction,
        ipOfficeRef
      });
    }
  };

  const handleCloseModal = () => {
    setSelectedFiling(null);
    setViewMode(false);
  };

  // Assignment handlers
  const handleAssignToMe = (applicationId) => {
    assignMutation.mutate({ applicationId, attorneyId: null }); // null = assign to self
  };

  const handleUnassign = (applicationId) => {
    unassignMutation.mutate(applicationId);
  };

  const handleOpenTransferModal = (application) => {
    setTransferModalApp(application);
  };

  const handleCloseTransferModal = () => {
    setTransferModalApp(null);
  };

  const handleTransfer = (applicationId, targetOrgId, reason) => {
    transferMutation.mutate(
      { applicationId, targetOrgId, reason },
      {
        onSuccess: () => {
          setTransferModalApp(null);
        }
      }
    );
  };

  // Recall handler - removes org transfer AND attorney assignment
  const handleRecall = (application) => {
    if (window.confirm('Recall this application back to MarkoTM? This will remove the external firm assignment and unassign the attorney.')) {
      recallMutation.mutate(application.id);
    }
  };

  // Staff assignment handlers
  const handleOpenAssignStaffModal = (application) => {
    setAssignStaffModalApp(application);
  };

  const handleCloseAssignStaffModal = () => {
    setAssignStaffModalApp(null);
  };

  const handleAssignToStaff = (applicationId, attorneyId) => {
    assignMutation.mutate(
      { applicationId, attorneyId },
      {
        onSuccess: () => {
          setAssignStaffModalApp(null);
        }
      }
    );
  };

  // Get unique countries from filings
  const countries = [...new Set(
    filings.flatMap(f => f.filings?.map(j => j.jurisdiction) || [])
  )];

  if (isLoading) {
    return (
      <div className="filing-queue">
        <div className="filing-queue-loading">
          {t('attorney.filingQueue.loading', 'Loading filing queue...')}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="filing-queue">
        <div className="filing-queue-error">
          {t('attorney.filingQueue.error', 'Error loading filing queue')}
        </div>
      </div>
    );
  }

  return (
    <div className="filing-queue">
      <div className="filing-queue-header">
        <h1>{t('attorney.filingQueue.title', 'Filing Queue')}</h1>

        <div className="filing-queue-filters">
          <select
            value={selectedCountry}
            onChange={(e) => setSelectedCountry(e.target.value)}
            className="filing-filter-select"
          >
            <option value="all">
              {t('attorney.filingQueue.allCountries', 'All Countries')}
            </option>
            {countries.map(country => (
              <option key={country} value={country}>{country}</option>
            ))}
          </select>

          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="filing-filter-select"
          >
            <option value="all">
              {t('attorney.filingQueue.allStatuses', 'All Statuses')}
            </option>
            <option value="pending">
              {t('attorney.filingQueue.statusPending', 'Pending')}
            </option>
            <option value="filed">
              {t('attorney.filingQueue.statusFiled', 'Filed')}
            </option>
          </select>

          <select
            value={assignmentFilter}
            onChange={(e) => setAssignmentFilter(e.target.value)}
            className="filing-filter-select"
          >
            <option value="all">
              {t('attorney.filingQueue.allAssignments', 'All Assignments')}
            </option>
            <option value="unassigned">
              {t('attorney.filingQueue.unassigned', 'Unassigned')}
            </option>
            <option value="mine">
              {t('attorney.filingQueue.assignedToMe', 'Assigned to Me')}
            </option>
            <option value="my_firm">
              {t('attorney.filingQueue.myFirm', 'My Firm')}
            </option>
          </select>

          <button
            className="filing-refresh-btn"
            onClick={() => queryClient.invalidateQueries({ queryKey: FILING_QUEUE_KEY })}
          >
            {t('attorney.filingQueue.refresh', 'Refresh')}
          </button>
        </div>
      </div>

      <div className="filing-queue-content">
        {filings.length === 0 ? (
          <div className="filing-queue-empty">
            <span className="filing-queue-empty-icon">📋</span>
            <p>{t('attorney.filingQueue.empty', 'No filings in queue')}</p>
          </div>
        ) : (
          <div className="filing-cards">
            {filings.map(application => {
              const isHighlighted = highlightedAppId &&
                (application.id === highlightedAppId || application.applicationId === highlightedAppId);
              return (
                <div
                  key={application.id}
                  ref={isHighlighted ? highlightedRef : null}
                >
                  <FilingCard
                    application={application}
                    onFileNow={handleFileNow}
                    onViewFiling={handleViewFiling}
                    onEditRef={handleEditRef}
                    isHighlighted={isHighlighted}
                    onAssignToMe={handleAssignToMe}
                    onUnassign={handleUnassign}
                    onTransfer={handleOpenTransferModal}
                    onRecall={handleRecall}
                    onAssignToStaff={canReassign ? handleOpenAssignStaffModal : null}
                    canTransferToFirm={canTransferToFirm}
                    canRecallFromFirm={canRecallFromFirm}
                    canReassign={canReassign}
                    currentAttorneyId={attorneyId}
                    isAssigning={assignMutation.isPending}
                    isUnassigning={unassignMutation.isPending}
                    isRecalling={recallMutation.isPending}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>

      {selectedFiling && (
        <FileNowModal
          application={selectedFiling.application}
          jurisdiction={selectedFiling.jurisdiction}
          onClose={handleCloseModal}
          onMarkFiled={handleMarkFiled}
          isLoading={markFiledMutation.isPending}
          viewMode={viewMode}
        />
      )}

      {transferModalApp && (
        <TransferToFirmModal
          application={transferModalApp}
          externalFirms={externalFirmsData?.firms || []}
          onClose={handleCloseTransferModal}
          onTransfer={handleTransfer}
          isLoading={transferMutation.isPending}
        />
      )}

      {assignStaffModalApp && (
        <AssignToStaffModal
          application={assignStaffModalApp}
          staffMembers={orgMembersData?.members || []}
          onClose={handleCloseAssignStaffModal}
          onAssign={handleAssignToStaff}
          isLoading={assignMutation.isPending}
          currentUserId={attorneyId}
        />
      )}
    </div>
  );
}

export default FilingQueue;
